import React, { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthShell } from "@/components/auth/AuthShell";
import { PasswordFields } from "@/components/auth/PasswordFields";
import api from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pwValid, setPwValid] = useState(false);
  const [loading, setLoading] = useState(false);

  const requestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/request-password-reset", { email });
      toast.success("If an account exists, a reset link has been sent.");
    } catch {
      toast.error("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const doReset = async (e) => {
    e.preventDefault();
    if (!pwValid) {
      toast.error("Please meet all password requirements and confirm your password.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated. Please log in.");
      navigate("/login");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset link is invalid or expired.");
    } finally {
      setLoading(false);
    }
  };

  if (token) {
    return (
      <AuthShell title="Set a new password" subtitle="Choose a new password for your account.">
        <form onSubmit={doReset} className="space-y-4">
          <PasswordFields
            password={password}
            setPassword={setPassword}
            onValidChange={setPwValid}
            idPrefix="reset"
            label="New password"
          />
          <Button type="submit" className="w-full" disabled={loading || !pwValid} data-testid="reset-submit-button">{loading ? "Saving…" : "Update password"}</Button>
        </form>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Reset your password"
      subtitle="Enter your email and we'll send a reset link."
      footer={<><Link to="/login" className="font-[560] text-primary hover:underline">Back to login</Link></>}
    >
      <form onSubmit={requestReset} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="reset-email-input" />
        </div>
        <Button type="submit" className="w-full" disabled={loading} data-testid="reset-request-button">{loading ? "Sending…" : "Send reset link"}</Button>
      </form>
    </AuthShell>
  );
}
